import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  Button, 
  Divider, 
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { registerUser } from '../../api/auth';
import { AuthContext } from '../../context/AuthContext';

const registerSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  role: Yup.string()
    .oneOf(['admin', 'manager', 'investor'], 'Invalid role')
    .required('Role is required'),
});

const Register = () => {
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    setError('');

    try {
      const result = await registerUser({
        name: values.name,
        email: values.email,
        role: values.role
      });
      
      if (result.success) {
        // Login the user after successful registration
        login(result.data);
        
        // Redirect based on user role
        if (values.role === 'admin') {
          navigate('/admin/dashboard');
        } else if (values.role === 'manager') {
          navigate('/manager/dashboard');
        } else if (values.role === 'investor') {
          navigate('/investor/dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
        setError(result.error || 'Registration failed. Please try again.');
      }
    } catch (error) {
      setError('An error occurred. Please try again later.');
      console.error('Registration error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className="auth-container">
      <Card className="auth-card">
        <CardContent>
          <Box className="auth-header">
            <img 
              src="/logo.png" 
              alt="HFRConnect Logo" 
              className="auth-logo"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/200x80?text=HFRConnect';
              }}
            />
            <Typography variant="h5" component="h1" gutterBottom>
              Register for HFRConnect
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Formik
            initialValues={{ name: '', email: '', role: '' }}
            validationSchema={registerSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values, handleChange, handleBlur }) => (
              <Form>
                <Box className="form-field">
                  <TextField
                    fullWidth
                    id="name"
                    name="name"
                    label="Full Name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                  />
                </Box>

                <Box className="form-field">
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Email Address"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    variant="outlined"
                  />
                </Box>

                <Box className="form-field">
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="role-label">Role</InputLabel>
                    <Select
                      labelId="role-label"
                      id="role"
                      name="role"
                      value={values.role}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.role && Boolean(errors.role)}
                      label="Role"
                    >
                      <MenuItem value="">Select a role</MenuItem>
                      <MenuItem value="admin">Administrator</MenuItem>
                      <MenuItem value="manager">Fund Manager</MenuItem>
                      <MenuItem value="investor">Investor</MenuItem>
                    </Select>
                    {touched.role && errors.role && (
                      <Typography color="error" variant="caption">
                        {errors.role}
                      </Typography>
                    )}
                  </FormControl>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isSubmitting}
                  className="auth-submit"
                >
                  {isSubmitting ? 'Registering...' : 'Register'}
                </Button>
              </Form>
            )}
          </Formik>

          <Divider className="auth-divider" />

          <Box className="auth-footer">
            <Typography variant="body2">
              Already have an account?{' '}
              <Link to="/login" style={{ color: '#1976d2' }}>
                Log In
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Register;
